"use client";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "next-themes";
import { NuqsAdapter } from "nuqs/adapters/next/app";
import { useState } from "react";
import { ApiError } from "@/lib/api";

/**
 * Client-side state providers, mounted once in the root layout.
 *
 * Three concerns, deliberately separate:
 *
 *  - **react-query** owns server state that varies while the app is open —
 *    items, filters, suggestions. The tenant's project is *not* one of these: it
 *    is fetched server-side and handed down through `ProjectProvider`.
 *  - **nuqs** backs the state that must survive a reload or a shared link
 *    (selected POI, page, filters), which the legacy app kept in Redux and
 *    hand-rolled URL-hash sync.
 *  - **next-themes** applies the tenant's light/dark preference by toggling the
 *    `dark` class Tailwind is configured for.
 */
function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Panning the map refetches items by bbox, so a short window of reuse
        // avoids a request per frame while still feeling live.
        staleTime: 30_000,
        // A 404 from the backend is an answer, not a blip — only retry once, and
        // never on a client error.
        retry: (failureCount, error) =>
          error instanceof ApiError && error.status < 500
            ? false
            : failureCount < 1,
        refetchOnWindowFocus: false,
      },
    },
  });
}

export function Providers({
  children,
  defaultTheme,
}: {
  children: React.ReactNode;
  /**
   * The tenant's preferred theme. Passed from the server rather than read from
   * the project context, because the theme provider sits above it — and it has to,
   * so the `dark` class is applied before anything inside renders.
   */
  defaultTheme?: string;
}) {
  // Created in state, not at module scope: a module-level client would be shared
  // across requests on the server and leak one tenant's data into another's.
  const [queryClient] = useState(makeQueryClient);

  return (
    <NuqsAdapter>
      <QueryClientProvider client={queryClient}>
        {/* A tenant that states a theme gets it; one that doesn't follows the
            device. `enableSystem` stays on either way so "system" remains a
            choice the user can make. */}
        <ThemeProvider
          attribute="class"
          defaultTheme={defaultTheme || "system"}
          enableSystem
          enableColorScheme
        >
          {children}
        </ThemeProvider>
      </QueryClientProvider>
    </NuqsAdapter>
  );
}
